import { useEffect, useRef, type RefObject } from "react";

/**
 * Report text selections that fall inside `containerRef` via `onSelect`, across every OS.
 *
 * We listen to the document `selectionchange` event rather than `mouseup`, because iOS/iPadOS
 * Safari (long-press + drag handles) and many touch browsers never fire a reliable `mouseup`
 * for a text selection — so the previous `onMouseUp` capture silently failed there. `selectionchange`
 * fires for mouse, touch and pen selection alike.
 *
 * `enabled` gates the listener (e.g. only while a "mark" mode is active). `onSelect` is kept in a
 * ref so the effect doesn't re-subscribe on every render.
 */
export function useSelectionWithin(
  containerRef: RefObject<HTMLElement | null>,
  enabled: boolean,
  onSelect: (text: string) => void,
): void {
  const cb = useRef(onSelect);
  cb.current = onSelect;

  useEffect(() => {
    if (!enabled || typeof document === "undefined") return;
    const handler = () => {
      const sel = window.getSelection?.();
      if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return;
      const text = sel.toString().trim();
      if (!text) return;
      const el = containerRef.current;
      if (!el) return;
      const anchor = sel.anchorNode;
      const node = anchor && anchor.nodeType === Node.TEXT_NODE ? anchor.parentNode : anchor;
      if (node && el.contains(node)) cb.current(text);
    };
    document.addEventListener("selectionchange", handler);
    return () => document.removeEventListener("selectionchange", handler);
  }, [containerRef, enabled]);
}
